// Client/src/pages/digi/Invoice/TemplateDesigner/StyleEditor.jsx
//
// Edits one Style object (see design.ts Style): typography first, box styling
// (background, border, corners, padding, opacity) behind a second section so
// the common choices stay in view. Clearing a value removes the key, so the
// element falls back to the page defaults.

import React from "react";
import { Field, NumberInput, SelectInput, Segmented, ColorInput, Toggle, BoxInput, Section } from "./controls";
import { FONT_FAMILIES } from "./designModel";

const clean = (s) => {
  const out = {};
  Object.entries(s || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (k === "border" && (!v || !Object.keys(v).length)) return;
    out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

const SIDES = ["top", "right", "bottom", "left"];

export default function StyleEditor({ style, onChange, palette, typography = true, box = true, valign = false, title = "Style", boxOpen = false }) {
  const s = style || {};
  const set = (patch) => onChange(clean({ ...s, ...patch }));
  const border = s.border || {};
  const firstSide = SIDES.map((k) => border[k]).find(Boolean) || { width: 1, color: "line", style: "solid" };
  const setBorder = (sides, side) => {
    const next = {};
    sides.forEach((k) => (next[k] = side));
    set({ border: Object.keys(next).length ? next : undefined });
  };
  const activeSides = SIDES.filter((k) => border[k]);

  return (
    <>
      {typography && (
        <Section title={title}>
          <Field label="Font">
            <SelectInput
              value={s.fontFamily || ""}
              placeholder="Page font"
              options={FONT_FAMILIES.map((f) => ({ value: f, label: f }))}
              onChange={(v) => set({ fontFamily: v || undefined })}
            />
          </Field>
          <div className="td-grid2">
            <Field label="Size">
              <NumberInput value={s.fontSize} allowEmpty placeholder="Page" min={6} max={72} step={0.5} unit="pt" onChange={(v) => set({ fontSize: v })} />
            </Field>
            <Field label="Weight">
              <SelectInput
                value={s.fontWeight ? String(s.fontWeight) : ""}
                placeholder="Normal"
                options={[
                  { value: "400", label: "Regular" },
                  { value: "500", label: "Medium" },
                  { value: "600", label: "Semibold" },
                  { value: "700", label: "Bold" },
                ]}
                onChange={(v) => set({ fontWeight: v ? Number(v) : undefined })}
              />
            </Field>
          </div>
          <div className="td-row-gap">
            <Toggle label="Italic" checked={s.italic} onChange={(v) => set({ italic: v || undefined })} />
            <Toggle label="Underline" checked={s.underline} onChange={(v) => set({ underline: v || undefined })} />
            <Toggle label="CAPS" checked={s.uppercase} onChange={(v) => set({ uppercase: v || undefined })} />
          </div>
          <Field label="Text colour">
            <ColorInput value={s.color} palette={palette} onChange={(v) => set({ color: v })} />
          </Field>
          <Field label="Align">
            <Segmented
              ariaLabel="Text alignment"
              value={s.align}
              onChange={(v) => set({ align: v })}
              options={[
                { value: "left", label: "Align left", icon: "align-left", toggle: true },
                { value: "center", label: "Centre", icon: "align-center", toggle: true },
                { value: "right", label: "Align right", icon: "align-right", toggle: true },
                { value: "justify", label: "Justify", icon: "align-justify", toggle: true },
              ]}
            />
          </Field>
          {valign && (
            <Field label="Vertical">
              <Segmented
                ariaLabel="Vertical alignment"
                value={s.valign}
                onChange={(v) => set({ valign: v })}
                options={[
                  { value: "top", label: "Top", toggle: true },
                  { value: "middle", label: "Middle", toggle: true },
                  { value: "bottom", label: "Bottom", toggle: true },
                ]}
              />
            </Field>
          )}
          <div className="td-grid2">
            <Field label="Line height">
              <NumberInput value={s.lineHeight} allowEmpty placeholder="Auto" min={0.8} max={3} step={0.05} onChange={(v) => set({ lineHeight: v })} />
            </Field>
            <Field label="Letter spacing">
              <NumberInput value={s.letterSpacing} allowEmpty placeholder="0" min={-2} max={10} step={0.25} unit="px" onChange={(v) => set({ letterSpacing: v })} />
            </Field>
          </div>
        </Section>
      )}
      {box && (
        <Section title="Box" defaultOpen={boxOpen}>
          <Field label="Background">
            <ColorInput value={s.background} palette={palette} onChange={(v) => set({ background: v })} />
          </Field>
          <Field label="Border sides">
            <div className="td-seg" role="group" aria-label="Border sides">
              {SIDES.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`td-seg__btn${border[k] ? " is-active" : ""}`}
                  aria-pressed={!!border[k]}
                  onClick={() => {
                    const sides = border[k] ? activeSides.filter((x) => x !== k) : [...activeSides, k];
                    setBorder(sides, firstSide);
                  }}
                >
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
              <button type="button" className="td-seg__btn" onClick={() => setBorder(SIDES, firstSide)}>
                All
              </button>
              <button type="button" className="td-seg__btn" onClick={() => setBorder([], firstSide)}>
                None
              </button>
            </div>
          </Field>
          {activeSides.length > 0 && (
            <>
              <div className="td-grid2">
                <Field label="Border width">
                  <NumberInput value={firstSide.width} min={0.5} max={6} step={0.5} unit="px" onChange={(v) => setBorder(activeSides, { ...firstSide, width: v || 1 })} />
                </Field>
                <Field label="Border style">
                  <SelectInput
                    value={firstSide.style}
                    options={[
                      { value: "solid", label: "Solid" },
                      { value: "dashed", label: "Dashed" },
                      { value: "dotted", label: "Dotted" },
                    ]}
                    onChange={(v) => setBorder(activeSides, { ...firstSide, style: v })}
                  />
                </Field>
              </div>
              <Field label="Border colour">
                <ColorInput value={firstSide.color} palette={palette} allowNone={false} onChange={(v) => setBorder(activeSides, { ...firstSide, color: v })} />
              </Field>
            </>
          )}
          <div className="td-grid2">
            <Field label="Rounded corners">
              <NumberInput value={s.radius} allowEmpty placeholder="0" min={0} max={40} unit="px" onChange={(v) => set({ radius: v })} />
            </Field>
            <Field label="Opacity">
              <NumberInput
                value={s.opacity === undefined ? undefined : Math.round(s.opacity * 100)}
                allowEmpty
                placeholder="100"
                min={0}
                max={100}
                step={5}
                unit="%"
                onChange={(v) => set({ opacity: v === undefined ? undefined : v / 100 })}
              />
            </Field>
          </div>
          <Field label="Inner padding (mm)">
            <BoxInput value={s.padding} onChange={(v) => set({ padding: v })} />
          </Field>
        </Section>
      )}
    </>
  );
}
